import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useEditProfile } from "@/hooks/use-edit-profile";
import Header from "@/components/Header";
import InputField from "@/components/Profile/InputField";
import MultiSelect from "@/components/Profile/multiselect/MultiSelect";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ProfileEditGeneralPage() {
    const { handleProfileUpdate, loading } = useEditProfile();
    const navigate = useNavigate();
    const { astrologer } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [astroData, setAstroData] = useState({
        name: "",
        description: "",
        phoneNumber: "",
        email: "",
        experience: "",
        languages: [],
        skills: [],
        expertise: [],
        picture: "",
        rate: "",
    });

    useEffect(() => {
        if (astrologer?.personalInformation) {
            setAstroData({
                name: astrologer.personalInformation.name || "",
                description: astrologer.personalInformation.description || "",
                phoneNumber: astrologer.personalInformation.phoneNumber || "",
                email: astrologer.personalInformation.email || "",
                experience: astrologer.stats?.experience || "",
                languages: astrologer.personalInformation.languages || [],
                skills: astrologer.personalInformation.astrologerTypes || [],
                expertise: astrologer.personalInformation.categories || [],
                picture: astrologer.personalInformation.picture || "",
                rate: astrologer.personalInformation.rate || 0,
            });
        }
    }, [astrologer]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setAstroData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert("File size exceeds 2MB limit");
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setAstroData((prevData) => ({
                    ...prevData,
                    picture: reader.result,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const fileInput = document.getElementById("profile-image-upload");
            const file = fileInput ? fileInput.files[0] : null;

            const updatedData = {
                ...astroData,
                skills: astroData.skills,
                expertise: astroData.expertise,
                languages: astroData.languages,
            };

            await handleProfileUpdate(updatedData, file);
        } catch (error) {
            console.error("Error saving profile:", error);
        } finally {
            setIsSubmitting(false);
            navigate("/profile");
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <form onSubmit={handleSave} className="max-w-[1200px] mx-auto px-4 py-8">
                {/* General Information Section */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold mb-8">General Information</h2>

                    <div className="grid grid-cols-1 md:grid-cols-[300px,1fr] gap-8">

                        <div className="space-y-6">
                            {/* Profile image and upload section */}
                            <div className="flex items-start gap-6">
                                {/* Profile image circle */}
                                <div className="relative">
                                    <img
                                        src={astroData.picture || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3"}
                                        alt="Profile"
                                        className="w-24 h-24 rounded-full object-cover border-2 border-[#E8D5C0]"
                                    />
                                </div>

                                {/* Upload button and text */}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        id="profile-image-upload"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        accept="image/*"
                                    />
                                    <label
                                        htmlFor="profile-image-upload"
                                        className="block w-full bg-[#F05E23] text-white text-center py-3 px-4 rounded-full cursor-pointer hover:bg-[#E04D12] transition-colors font-medium text-lg"
                                    >
                                        Upload Photo
                                    </label>
                                    <p className="text-sm text-gray-500 text-center mt-2">Max size is 2 Mb</p>
                                </div>
                            </div>

                            {/* Session charges section */}
                            <div className="mt-8">
                                <label className="block mb-4 text-gray-700 text-lg font-medium">
                                    Per Session Charges for 30min <span className="text-[#F05E23]">*</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                                            <span className="absolute left-4 text-gray-500">Rs.</span>
                                            <input
                                                type="number"
                                                name="rate"
                                                value={astroData.rate}
                                                onChange={handleInputChange}
                                                className="w-40 pl-12 pr-4 py-3 focus:outline-none"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <span className="text-gray-500 text-lg font-medium">Rs {astroData.rate} /min</span>
                                </div>
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="space-y-6">
                            <div>
                                <label className="block mb-2">
                                    Display Name <span className="text-[#F05E23]">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={astroData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter the name you want to show"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#F05E23]"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block mb-2">
                                    Bio <span className="text-[#F05E23]">*</span>
                                </label>
                                <textarea
                                    name="description"
                                    value={astroData.description}
                                    onChange={handleInputChange}
                                    placeholder="Tell us about yourself"
                                    rows={5}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#F05E23] resize-none"
                                    required
                                    maxLength={500}
                                />
                                <div className="text-right text-sm text-gray-500">
                                    {astroData.description?.length || 0}/500
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block mb-2">
                                Experience <span className="text-[#F05E23]">*</span>
                            </label>
                            <input
                                type="number"
                                name="experience"
                                value={astroData.experience}
                                onChange={handleInputChange}
                                placeholder="Enter your years of experience"
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#F05E23]"
                                required
                            />
                        </div>

                        <div>
                            <label className="block mb-2">
                                Languages <span className="text-[#F05E23]">*</span>
                            </label>
                            <MultiSelect
                                items={languagesOptions}
                                label=""
                                selectedItems={astroData.languages}
                                astroData={astroData}
                                setastroData={setAstroData}
                            />
                        </div>

                        <div>
                            <label className="block mb-2">
                                Skills <span className="text-[#F05E23]">*</span>
                            </label>
                            <MultiSelect
                                items={astrologerTypesOptions}
                                label=""
                                selectedItems={astroData.skills}
                                astroData={astroData}
                                setastroData={setAstroData}
                            />
                        </div>

                        <div>
                            <label className="block mb-2">
                                Expertise <span className="text-[#F05E23]">*</span>
                            </label>
                            <MultiSelect
                                items={categoriesOptions}
                                label=""
                                selectedItems={astroData.expertise}
                                astroData={astroData}
                                setastroData={setAstroData}
                            />
                        </div>
                    </div>
                </div>

                {/* Contact Details Section */}
                <div className="mb-12">
                    <h2 className="text-2xl font-semibold mb-8">Contact Details</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        <div>
                            <label className="block mb-2">
                                Mobile Number <span className="text-[#F05E23]">*</span>
                            </label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={astroData.phoneNumber}
                                onChange={handleInputChange}
                                placeholder="+91"
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#F05E23]"
                                required
                            />
                        </div>

                        <div>
                            <label className="block mb-2">
                                Email <span className="text-[#F05E23]">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={astroData.email}
                                onChange={handleInputChange}
                                placeholder="name@example.com"
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#F05E23]"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-[#F05E23] text-white px-12 py-3 rounded-full text-lg font-medium hover:bg-[#E04D12] transition-colors"
                    >
                        Save
                    </button>
                </div>
            </form>
        </div>
    );
}

// Predefined Options
const astrologerTypesOptions = [
    { value: "Vedic", label: "Vedic" },
    { value: "Vaastu", label: "Vaastu" },
    { value: "Lal kitab", label: "Lal kitab" },
    { value: "Tarot", label: "Tarot" },
    { value: "Numerology", label: "Numerology" },
    { value: "Life coach", label: "Life coach" },
    { value: "Psychic", label: "Psychic" },
    { value: "KP", label: "KP" },
    { value: "Face reading", label: "Face reading" },
];

const categoriesOptions = [
    { value: "Career", label: "Career" },
    { value: "Finance", label: "Finance" },
    { value: "Addiction", label: "Addiction" },
    { value: "Anxiety", label: "Anxiety" },
    { value: "Child Birth", label: "Child Birth" },
    { value: "Family Conflicts", label: "Family Conflicts" },
    { value: "Family Problems", label: "Family Problems" },
    { value: "Foreign Travel", label: "Foreign Travel" },
    { value: "Life Transitions", label: "Life Transitions" },
    { value: "Couple Counselling", label: "Couple Counselling" },
    { value: "Anger/Aggression", label: "Anger/Aggression" },
    { value: "Business", label: "Business" },
    { value: "Business Deals", label: "Business Deals" },
    { value: "Child Name", label: "Child Name" },
    { value: "Education", label: "Education" },
    { value: "Gemstone", label: "Gemstone" },
    { value: "Government Job", label: "Government Job" },
    { value: "Job", label: "Job" },
    { value: "Legal Issues", label: "Legal Issues" },
    { value: "Legal Matters", label: "Legal Matters" },
    { value: "Love", label: "Love" },
    { value: "Marriage", label: "Marriage" },
    { value: "Relationships", label: "Relationships" },
    { value: "Remedies", label: "Remedies" },
    { value: "Shubh Muhurat", label: "Shubh Muhurat" },
    { value: "Stock Market", label: "Stock Market" },
    { value: "Vastu", label: "Vastu" },
    { value: "Wealth & Property", label: "Wealth & Property" },
    { value: "Depression", label: "Depression" },
    { value: "Health", label: "Health" },
    { value: "Mental Health", label: "Mental Health" },
    { value: "Mood Swings", label: "Mood Swings" },
    { value: "Obesity & eating disorders", label: "Obesity & eating disorders" },
    { value: "Relationship", label: "Relationship" },
    { value: "Guilt", label: "Guilt" },
    { value: "Grief", label: "Grief" },
];

const languagesOptions = [
    { value: "English", label: "English" },
    { value: "Hindi", label: "Hindi" },
    { value: "Marathi", label: "Marathi" },
    { value: "Tamil", label: "Tamil" },
    { value: "Telugu", label: "Telugu" },
    { value: "Kannada", label: "Kannada" },
    { value: "Malayalam", label: "Malayalam" },
    { value: "Gujarati", label: "Gujarati" },
    { value: "Punjabi", label: "Punjabi" },
    { value: "Bengali", label: "Bengali" },
    { value: "Urdu", label: "Urdu" },
];